import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // CRITICAL: Start with loading=false to NOT block initial render
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Check auth in background, don't block render
    checkAuthAsync();
  }, []);

  const checkAuthAsync = async () => {
    const token = localStorage.getItem('aura_token');
    if (token) {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000 // 5s timeout max
        });
        setUser(response.data);
        // Fetch usage in background, don't await
        fetchUsage(token);
      } catch (error) {
        localStorage.removeItem('aura_token');
        setUser(null);
      }
      setLoading(false);
    }
    setAuthChecked(true);
  };

  const fetchUsage = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/usage`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('aura_token')}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token } = response.data;
    localStorage.setItem('aura_token', access_token);
    
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);
    fetchUsage(access_token);
    return userResponse.data;
  };

  const register = async (email, password, company_name) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      company_name 
    });
    const { access_token } = response.data;
    localStorage.setItem('aura_token', access_token);
    
    const userResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);
    fetchUsage(access_token);
    return userResponse.data;
  };

  const logout = () => {
    localStorage.removeItem('aura_token');
    setUser(null);
    setUsage(null);
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('aura_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      usage,
      loading, 
      authChecked,
      login, 
      register, 
      logout, 
      getAuthHeader,
      refreshUsage: () => fetchUsage(),
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
